import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import UserAvatar from './UserAvatar';
import {
	setActiveTeamConversation,
	markConversationAsRead,
} from '../../../actions/teams';
import { CustomButton } from '@/components/ui/custom/CustomButton';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Star } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

const TeamsSidebarSelector = ({
	teamConversationType,
	setTeamConversationType,
	handleDashboardChange,
	navigateToComponent,
}) => {
	// list of all conversations (DMs and Groups)
	const teams = useSelector((state) => state.teams);
	const [loading, setIsLoading] = useState(true); // Start with loading state
	const auth = useSelector((state) => state.auth);
	const users = useSelector((state) => state.users);
	const dispatch = useDispatch();

	useEffect(() => {
		if (teams.teamConversations && loading) {
			setIsLoading(false); // Set loading to false when the data is available
		}
	}, [loading, teams.teamConversations]);

	// returns a list of conversations filtered by the type (direct message or group)
	const getFilteredConversations = () => {
		return teams?.teamConversations?.filter((conversation) =>
			teamConversationType === 'DIRECT_MESSAGES'
				? conversation.isDirectMessage
				: !conversation.isDirectMessage
		);
	};

	const sortConversationsByLatestMessageDate = () => {
		const allConversations = getFilteredConversations();

		return allConversations?.sort(
			(a, b) =>
				new Date(b.latestMessageDate) - new Date(a.latestMessageDate)
		);
	};

	// returns the user object from a direct message that is not the currently authenticated user
	const getUserFromDMThatIsNotAuth = (conversation) => {
		const _filteredUser = conversation._users.filter(
			(_user) => _user !== auth.user._id
		)[0];

		return users.find((user) => user._id === _filteredUser);
	};

	const sortAllConversations = () => {
		const allConversations = sortConversationsByLatestMessageDate();

		if (!allConversations || allConversations.length === 0) {
			return null;
		}

		const unreadConversations = allConversations?.filter((conversation) => {
			return conversation._unreadUsers.includes(auth.user._id);
		});

		if (unreadConversations.length === 0) {
			// No unread conversations, return all conversations sorted by latestMessageDate
			return allConversations;
		}

		// Sort unread conversations by latestMessageDate in descending order
		const sortedUnreadConversations = unreadConversations.sort(
			(a, b) =>
				new Date(b.latestMessageDate) - new Date(a.latestMessageDate)
		);

		const sortedReadConversations = allConversations?.filter(
			(conversation) => {
				return (
					conversation._unreadUsers.includes(auth.user._id) === false
				);
			}
		);

		// Combine unread and read conversations, with unread first
		return [...sortedUnreadConversations, ...sortedReadConversations];
	};

	const starredConversations = sortAllConversations()?.filter(
		(conversation) => conversation._starredUsers?.includes(auth.user._id)
	);
	const regularConversations = sortAllConversations()?.filter(
		(conversation) => !conversation._starredUsers?.includes(auth.user._id)
	);

	const generateConversationUrl = (conversation) => {
		const type = conversation.isDirectMessage ? 'dms' : 'groups';
		return `/groupchat/${type}/conversation/groups/${
			conversation._id
		}?_teamMessage=${
			conversation?.teamMessages?.[0]?._id ?? ''
		}&_location=${conversation._business}&isDirectMessage=${
			conversation.isDirectMessage
		}`;
	};

	const renderConversationButton = (conversation) => {
		const isDirectMessage = conversation.isDirectMessage;
		const userFromDM = isDirectMessage
			? getUserFromDMThatIsNotAuth(conversation)
			: null;

		return (
			<CustomButton
				key={conversation._id}
				className={cn(
					'flex justify-start w-full items-center',
					teams._activeTeamConversation === conversation._id
						? 'bg-accent text-accent-foreground'
						: ''
				)}
				onClick={() => {
					setTeamConversationType(
						isDirectMessage ? 'DIRECT_MESSAGES' : 'GROUPS'
					);
					handleDashboardChange(
						isDirectMessage ? 'DIRECT_MESSAGES' : 'GROUPS'
					);
					dispatch(setActiveTeamConversation(conversation._id));
					if (conversation._unreadUsers.includes(auth.user._id)) {
						dispatch(
							markConversationAsRead(conversation, auth.user._id)
						);
					}
					// Navigate to the conversation
					const conversationUrl =
						generateConversationUrl(conversation);
					navigateToComponent(conversationUrl);
				}}
			>
				{isDirectMessage ? (
					<UserAvatar
						fontWeightThin={
							!conversation._unreadUsers.includes(auth.user._id)
						}
						nameFontSize=".875rem"
						avatarFontSize="12px"
						avatarHeight="25px"
						avatarWidth="25px"
						showNameBelow={false}
						user={userFromDM}
					/>
				) : (
					<p
						className={cn(
							conversation._unreadUsers.includes(auth.user._id)
								? 'font-bold'
								: 'font-normal',
							'text-wrap w-full text-ellipsis text-left line-clamp-2 break-words'
						)}
					>
						{conversation.title}
					</p>
				)}
			</CustomButton>
		);
	};

	const hasStarredConversations = starredConversations?.length > 0;

	return (
		<div className="flex flex-col space-y-1.5 p-2 w-full">
			{loading ? (
				<div className="space-y-2 w-full" style={{ padding: '10px' }}>
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-full" />
					<Skeleton className="h-4 w-full" />
				</div>
			) : (
				<>
					{hasStarredConversations && (
						<div className="flex flex-col">
							<div className="flex space-x-2 items-center pl-2 pb-2">
								<span>
									<Star className="size-4" />
								</span>
								<p className="text-sm">Starred</p>
							</div>
							{starredConversations.map(renderConversationButton)}
							<Separator className="my-2" />
						</div>
					)}
					{regularConversations?.length > 0 ? (
						regularConversations.map(renderConversationButton)
					) : (
						<div className="text-start text-muted-foreground p-2">
							<p>No conversations yet</p>
							<p className="text-sm">
								Start a new conversation to connect with your
								team!
							</p>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default TeamsSidebarSelector;
